/**
 * I passi del tutorial guidato dell'elenco pubblicazioni eBay.
 *
 * Fratello di quello Amazon: stessa pagina, stessi pulsanti Scarica/Carica e stesse
 * schede - sono gli stessi componenti - e stesso menu a tre pallini. Cambia quello che le due piattaforme
 * fanno davvero, ed e' li' che i testi divergono.
 *
 * I bersagli della pagina sono scoped; le voci dei menu no, perche' Vuetify
 * stacca il contenuto dei menu e lo appende a `[data-app]`, fuori dalla pagina.
 */
const at = name => `[data-tour-scope="ebay-list"] [data-tour="${name}"]`;

const CATALOG_DOWNLOAD = '[data-tour="catalog-download"]';
const CATALOG_IMPORT = '[data-tour="catalog-import"]';
const CATALOG_UNMATCHED = '[data-tour="tab-unmatched"]';
const CATALOG_PRICE_ANALYSIS = '[data-tour="tab-price-analysis"]';
const ROW_SYNC = '[data-tour="row-sync"]';
const ROW_DELETE = '[data-tour="row-delete"]';
const ROW_EDIT = '[data-tour="row-edit"]';
const ROW_DETAIL = '[data-tour="row-detail"]';

function isVisible(selector) {
	const element = document.querySelector(selector);

	if (!element || element.getClientRects().length === 0) {
		return false;
	}

	/*
	 * Un menu che si sta chiudendo resta a schermo per tutta l'animazione, ma
	 * Vuetify gli toglie subito `menuable__content__active`. Senza questo
	 * controllo un menu in chiusura sembrerebbe ancora aperto e il click di
	 * pulizia, invece di chiuderlo, lo riaprirebbe.
	 */
	const content = element.closest('.v-menu__content');

	return !content || content.classList.contains('menuable__content__active');
}

/**
 * Lascia scattare la chiusura che Vuetify ha gia' in coda.
 *
 * I menu si chiudono al primo click fuori, e il click su "Avanti" e' fuori: la
 * direttiva l'ha registrato in cattura - prima ancora che il pulsante eseguisse
 * il proprio handler - e ha accodato la chiusura con un `setTimeout(0)`.
 * Riaprendo subito, il menu si richiuderebbe un istante dopo.
 */
function afterPendingClose() {
	return new Promise(resolve => setTimeout(resolve, 0));
}

/**
 * Aspetta che il menu abbia finito di aprirsi o chiudersi.
 *
 * L'attesa sta qui e non nel `timeout` del passo: cosi' si aspetta solo quando
 * c'e' davvero un menu da aprire, e a elenco vuoto il passo si aggancia subito
 * in basso invece di far aspettare a vuoto.
 */
async function waitFor(probe, wanted, timeout = 1500) {
	const deadline = performance.now() + timeout;

	while (performance.now() < deadline) {
		if (isVisible(probe) === wanted) {
			return;
		}

		await new Promise(resolve => setTimeout(resolve, 50));
	}
}

async function openMenu(activator, probe) {
	await afterPendingClose();

	if (isVisible(probe)) {
		return;
	}

	const element = document.querySelector(activator);

	if (!element) {
		return;
	}

	element.click();
	await waitFor(probe, true);
}

async function closeMenu(activator, probe) {
	await afterPendingClose();

	if (!isVisible(probe)) {
		return;
	}

	const element = document.querySelector(activator);

	// L'attivatore fa da interruttore: il secondo click richiude.
	if (element) {
		element.click();
		await waitFor(probe, false);
	}
}

// Sempre la prima riga: `querySelector` prende quella, ed e' l'unica che serve.
const openRow = () => openMenu(at('row-menu'), ROW_SYNC);

/** Richiude tutto: vale a fine giro e sui passi che devono trovare la pagina pulita. */
export async function stopEbayListTourMenus() {
	await closeMenu(at('row-menu'), ROW_SYNC);
}

export function createEbayListTourSteps() {
	return [
		{
			body: "Qui c'e' l'elenco di quello che hai pubblicato su eBay: una riga per ogni prodotto su ogni sito eBay, quindi lo stesso articolo compare piu' volte se lo vendi in piu' paesi. In alto trovi Scarica catalogo e Carica modifiche, che lavorano su tutte le pubblicazioni insieme, e sotto il titolo le schede delle altre pagine della sezione; i tre pallini a inizio riga invece agiscono su una sola.",
			key: 'intro',
			placement: 'bottom',
			target: at('tutorial'),
			title: 'Le pubblicazioni su eBay',
		},
		{
			body: "La ricerca parte da tre caratteri: sotto quella soglia il filtro si azzera e torna l'elenco intero. Cerca marketplace restringe a un sito eBay. Status distingue le tre situazioni di una pubblicazione: Confirmed e' stata accettata da eBay ed e' l'unica su cui funziona la modifica diretta; Pending e' partita e si aspetta ancora la risposta; Void e' quella non piu' attiva, rifiutata o chiusa.",
			key: 'filters',
			placement: 'bottom',
			target: at('filters'),
			title: 'Cercare e filtrare',
		},
		{
			body: "La colonna Prodotto arriva dal tuo catalogo - nome, sku, ean, giacenza, prezzo - la colonna eBay dall'inserzione: il sito, il link che la apre, l'Item ID e il Seller Sku. Prezzo, quantita' e spedizione nell'ultima colonna, Prezzi eBay, si scrivono direttamente qui, ma attenzione: scriverli non basta e non parte niente da solo. Sono i valori che verranno mandati quando userai Modifica su cms/marketplace dal menu della riga - lo vediamo fra poco. La valuta invece non si tocca: e' quella del sito eBay.",
			key: 'table',
			placement: 'top',
			target: at('table'),
			timeout: 250,
			title: 'Come si legge una riga',
		},
		{
			body: "Non riguarda la riga su cui sei: lavora su tutte le pubblicazioni di un sito eBay. Chiede il sito eBay e il formato, Excel o CSV. Nell'elenco compaiono solo i siti dove hai davvero pubblicazioni, col numero accanto, cosi' non si sceglie a vuoto. Il file nasce dal report ufficiale di eBay e puo' metterci qualche minuto: non devi restare ad aspettare, ti arrivano una mail e una notifica con il link per scaricarlo.",
			key: 'catalog-download',
			placement: 'bottom',
			target: CATALOG_DOWNLOAD,
			title: 'Scarica catalogo',
		},
		{
			body: "E' lo stesso file di prima, ricaricato dopo averlo modificato, ed e' la strada buona quando le inserzioni da ritoccare sono tante: una per una, da questa pagina, si va per le lunghe. Si leggono solo tre colonne - NuovoPrezzo, NuovaQt, NuovoStatus - e le righe lasciate vuote non vengono toccate. Prima di applicare passi sempre dall'anteprima, che conta quanti prezzi, quante giacenze, quante chiusure e quante righe sono state scartate e perche': a quel punto non e' stato scritto ancora niente. Nell'anteprima togli la spunta alle righe che non vuoi, poi Applica manda tutte le altre insieme a eBay in un unico file; il catalogo si aggiorna solo per quelle che eBay accetta. NuovoStatus puo' solo chiudere un'inserzione, per riaprirla va ripubblicato il prodotto.",
			key: 'catalog-import',
			placement: 'bottom',
			target: CATALOG_IMPORT,
			title: 'Carica modifiche',
		},
		{
			body: "E' una scheda a se', perche' ci si lavora a lungo: l'elenco delle inserzioni che stanno su eBay ma non risultano collegate a nessun prodotto del tuo catalogo - tipicamente roba pubblicata prima, o da un altro strumento. Una cosa da sapere: eBay non dice a quale sito appartiene ogni inserzione, quindi l'elenco e' unico per tutti i paesi e la valuta accanto al prezzo e' l'indizio per riconoscerle.",
			key: 'catalog-unmatched',
			placement: 'bottom',
			target: CATALOG_UNMATCHED,
			title: 'Associa prodotti',
		},
		{
			body: "Anche questa e' una scheda: le tue inserzioni eBay, dove segui con una stellina i prodotti che ti interessano. Su eBay la buybox non esiste: Ricerca concorrenti cerca, per ogni prodotto seguito, le inserzioni degli altri venditori con lo stesso EAN e confronta i totali che paga il cliente, prezzo piu' spedizione, dicendoti chi e' il piu' economico e in che posizione sei. Serve l'EAN sul prodotto, e c'e' un limite giornaliero di ricerche: per questo va in coda, ti dice prima quanto ci mettera' e ti avvisa alla fine. Aggiusta prezzo manda subito il nuovo prezzo a eBay, anche sulle inserzioni create con altri strumenti.",
			key: 'catalog-price-analysis',
			placement: 'bottom',
			target: CATALOG_PRICE_ANALYSIS,
			title: 'Analisi prezzi',
		},
		{
			body: "Le ultime due schede lavorano da sole, senza che tu debba toccare le righe una per una. Regole feed cambia prezzo e quantita' che mandiamo a eBay quando un prodotto rispetta delle condizioni - per esempio piu' 5% su una marca, o quantita' a zero sotto i due pezzi - e ogni regola si accende e si spegne con un interruttore. Repricing insegue i concorrenti dentro un prezzo minimo e uno massimo che decidi tu.",
			key: 'tab-rules',
			placement: 'bottom',
			target: at('tab-rules'),
			title: 'Regole feed e Repricing',
		},
		{
			body: "Passiamo all'altro menu. I tre pallini a inizio riga aprono quattro voci che valgono solo per quella pubblicazione. Due delle quattro cancellano qualcosa, e non la stessa cosa: vale la pena sapere esattamente quale fa cosa prima di usarle.",
			before: stopEbayListTourMenus,
			key: 'row-menu',
			placement: 'right',
			target: at('row-menu'),
			timeout: 250,
			title: 'Il menu della singola riga',
		},
		{
			body: "Toglie il collegamento fra il prodotto del tuo catalogo e la pubblicazione: la riga sparisce da questo elenco e smetti di gestire quell'inserzione da qui. Su eBay l'inserzione resta al suo posto e continua a vendere. Si usa quando il collegamento e' sbagliato, o quando vuoi che quel prodotto non venga piu' aggiornato in automatico.",
			before: openRow,
			key: 'row-sync',
			placement: 'right',
			target: ROW_SYNC,
			timeout: 250,
			title: 'Elimina sincronizzazione',
		},
		{
			body: "Questa cancella davvero. Chiede conferma, poi - se la riga e' Confirmed, cioe' se l'inserzione su eBay esiste per davvero - chiede a eBay di chiuderla, e in ogni caso toglie la riga da qui. Non confonderla con la voce sopra: quella scollega e lascia l'inserzione in vendita, questa la fa sparire anche da eBay.",
			before: openRow,
			key: 'row-delete',
			placement: 'right',
			target: ROW_DELETE,
			timeout: 250,
			title: 'Elimina da cms/marketplace',
		},
		{
			body: "Ecco dove finiscono i valori che hai scritto in riga. Questa voce manda a eBay prezzo, quantita' e spedizione cosi' come li vedi scritti in quella riga, subito, senza passare dalla coda. Compare solo sulle righe Confirmed, perche' e' l'unico caso in cui eBay accetta una modifica. Se qualcosa non va te lo dice a schermo; se va a buon fine la riga si ricarica con i valori nuovi.",
			before: openRow,
			key: 'row-edit',
			placement: 'right',
			target: ROW_EDIT,
			timeout: 250,
			title: 'Modifica su cms/marketplace',
		},
		{
			body: "L'ultima voce non tocca niente su eBay: apre la scheda completa del prodotto del catalogo, la stessa che trovi nella sezione Prodotti. Serve per andare a vedere o correggere il prodotto - titolo, immagini, giacenza reale - non la pubblicazione.",
			before: openRow,
			key: 'row-detail',
			placement: 'right',
			target: ROW_DETAIL,
			timeout: 250,
			title: 'Visualizza dettaglio prodotto',
		},
	];
}
